import type { IChallenge, IObjective } from "@bbj-types/challenge";
import { tz } from "@date-fns/tz";
import { yupResolver } from "@hookform/resolvers/yup";
import { useI18nContext } from "@root/utils/organisation";
import { challengeSchema, objectiveSchema } from "@root/utils/resolvers/challenge";
import { startOfDay } from "date-fns";
import { useForm } from "react-hook-form";
import { date, object, string } from "yup";
import type { IResolverCreateCandidateChallenge } from "../logics/types";

export interface IFormCreateCandidateChallenge {
  challenge: IChallenge | null;
  description: string;
  objective: IObjective | null;
  objectiveTitle: string;
  objectiveEndDate: Date | null;
}

const buildResolver = (timeZone: string) =>
  object({
    challenge: challengeSchema.required("La thématique est obligatoire"),
    description: string().max(300, "La description ne peut excéder 300 caractères"),
    objective: objectiveSchema.required("Le type d'objectif est obligatoire"),
    objectiveTitle: string().max(80, "Le titre ne peut excéder 80 caractères"),
    objectiveEndDate: date()
      .nullable()
      .typeError("Veuillez sélectionner une date valide")
      .test((value, context) => {
        if (value instanceof Date && value.getTime() < startOfDay(new Date(), { in: tz(timeZone) }).getTime()) {
          throw context.createError({
            path: context.path,
            message: "La date butoir doit être supérieure ou égale à la date du jour",
          });
        }
        return true;
      }),
  });

const FORM_DEFAULT_VALUES = {
  challenge: null,
  description: "",
  objective: null,
  objectiveTitle: "",
  objectiveEndDate: null,
};

export const useFormCreateCandidateChallenge = () => {
  const { timeZone } = useI18nContext();
  const form = useForm<IFormCreateCandidateChallenge, void, IResolverCreateCandidateChallenge>({
    defaultValues: FORM_DEFAULT_VALUES,
    // biome-ignore lint/suspicious/noExplicitAny: Ignore resolver bad typing
    resolver: yupResolver(buildResolver(timeZone)) as any,
  });

  return form;
};
